import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";

import { ScsVendorSendSection } from "./ScsVendorSendSection";
import {
  fetchInternalPottingPending,
  fetchInternalPottingReworkPending,
  fetchPottingSendOperators,
  fetchScsPottingSendDraft,
  POTTING_SEND_INTERNAL_PENDING_KEY,
  POTTING_SEND_OPERATORS_KEY,
  POTTING_SEND_REWORK_INTERNAL_PENDING_KEY,
  POTTING_SEND_SCS_DRAFT_KEY,
  type InternalPottingPendingRow,
  type InternalPottingReworkPendingRow,
} from "./pottingSendApi";

import { cn } from "@/lib/utils";

type PottingPageMainTab = "scs" | "sgs";

function PottingTabCountBadge({ count, tone = "muted" }: { count: number; tone?: "muted" | "info" | "warning" }) {
  if (count <= 0) {
    return null;
  }
  return (
    <Badge tone={tone} className="ml-1.5 tabular-nums">
      {count}
    </Badge>
  );
}

function InternalPottingSendPanel({ embedded = false }: { embedded?: boolean }) {
  const pendingQuery = useQuery({
    queryKey: POTTING_SEND_INTERNAL_PENDING_KEY,
    queryFn: fetchInternalPottingPending,
    refetchInterval: 30_000,
  });
  const pendingRows = pendingQuery.data?.rows ?? [];

  const body = (
    <>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={pendingRows.length > 0 ? "info" : "muted"}>
            {pendingRows.length} awaiting Final QC grade
          </Badge>
          <p className="text-sm text-slate-600">
            Route units on{" "}
            <Link to="/manufacturing/pre-encapsulation" className="font-medium text-brand-700 hover:underline">
              Pre-Encapsulation
            </Link>{" "}
            with the <strong>Internal</strong> path — they advance to the Potting station with no vendor send step.
            Grade on{" "}
            <Link to="/manufacturing/final-qc" className="font-medium text-brand-700 hover:underline">
              Final QC
            </Link>{" "}
            when they return from in-house potting.
          </p>
        </div>

        {pendingRows.length > 0 ? (
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Serial</th>
                  <th className="px-3 py-2 text-left">Kind</th>
                  <th className="px-3 py-2 text-left">Finished SKU</th>
                </tr>
              </thead>
              <tbody>
                {pendingRows.map((row: InternalPottingPendingRow) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs">{row.serial}</td>
                    <td className="px-3 py-2 text-slate-600">{row.serialKind}</td>
                    <td className="px-3 py-2 text-slate-600">{row.finishedSku ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500">No units are awaiting in-house potting grade right now.</p>
        )}
      </div>
    </>
  );

  if (embedded) {
    return (
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <p className="text-sm text-slate-500">
            In-house potting at SGS without a Megaventory purchase order. Units return through{" "}
            <Link to="/manufacturing/final-qc" className="font-medium text-brand-700 hover:underline">
              Final QC
            </Link>{" "}
            for grading.
          </p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={pendingQuery.isFetching}
            onClick={() => void pendingQuery.refetch()}
          >
            {pendingQuery.isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="h-4 w-4" aria-hidden />
            )}
            Refresh
          </Button>
        </CardHeader>
        <CardBody className="space-y-4">{body}</CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Internal potting (no PO)</h2>
          <p className="mt-1 text-sm text-slate-500">
            In-house potting at SGS — route on Pre-Encapsulation (Internal path). No vendor send or cartons.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={pendingQuery.isFetching}
          onClick={() => void pendingQuery.refetch()}
        >
          {pendingQuery.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-4 w-4" aria-hidden />
          )}
          Refresh
        </Button>
      </CardHeader>
      <CardBody className="space-y-4">{body}</CardBody>
    </Card>
  );
}

function InternalPottingReworkSendPanel() {
  const pendingQuery = useQuery({
    queryKey: POTTING_SEND_REWORK_INTERNAL_PENDING_KEY,
    queryFn: fetchInternalPottingReworkPending,
    refetchInterval: 30_000,
  });
  const pendingRows = pendingQuery.data?.rows ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Internal potting rework</h2>
          <p className="mt-1 text-sm text-slate-500">
            Devices routed from{" "}
            <Link to="/manufacturing/qc-incidents" className="font-medium text-brand-700 hover:underline">
              QC Incidents
            </Link>{" "}
            for in-house rework. Scan at{" "}
            <Link to="/manufacturing/pre-encapsulation" className="font-medium text-brand-700 hover:underline">
              Pre-Encapsulation
            </Link>{" "}
            — they advance to Potting with no vendor send step. Regrade on Final QC or QC Incidents when they return.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={pendingQuery.isFetching}
          onClick={() => void pendingQuery.refetch()}
        >
          {pendingQuery.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-4 w-4" aria-hidden />
          )}
          Refresh
        </Button>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="space-y-2">
          <Badge tone={pendingRows.length > 0 ? "info" : "muted"}>
            {pendingRows.length} awaiting regrade
          </Badge>
          {pendingRows.length > 0 ? (
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Serial</th>
                    <th className="px-3 py-2 text-left">Incident</th>
                    <th className="px-3 py-2 text-left">Finished SKU</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRows.map((row: InternalPottingReworkPendingRow) => (
                    <tr key={row.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs">{row.serial}</td>
                      <td className="px-3 py-2 text-slate-600">
                        #{row.incidentId}
                        {row.issue ? ` — ${row.issue}` : ""}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{row.finishedSku ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-500">No units are awaiting in-house potting rework regrade right now.</p>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export function PottingSendPage() {
  const [mainTab, setMainTab] = useState<PottingPageMainTab>("scs");

  const operatorsQuery = useQuery({
    queryKey: POTTING_SEND_OPERATORS_KEY,
    queryFn: fetchPottingSendOperators,
    staleTime: 60_000,
  });
  const scsDraftQuery = useQuery({
    queryKey: POTTING_SEND_SCS_DRAFT_KEY,
    queryFn: () => fetchScsPottingSendDraft({ background: true }),
    staleTime: 30_000,
  });
  const internalPendingQuery = useQuery({
    queryKey: POTTING_SEND_INTERNAL_PENDING_KEY,
    queryFn: fetchInternalPottingPending,
    refetchInterval: 30_000,
  });
  const internalReworkPendingQuery = useQuery({
    queryKey: POTTING_SEND_REWORK_INTERNAL_PENDING_KEY,
    queryFn: fetchInternalPottingReworkPending,
    refetchInterval: 30_000,
  });

  const operators = operatorsQuery.data?.operators ?? [];
  const scsWaitingForPo = scsDraftQuery.data?.waitingForPoCount ?? 0;
  const sgsAwaitingGrade =
    (internalPendingQuery.data?.rows.length ?? 0) + (internalReworkPendingQuery.data?.rows.length ?? 0);

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint="Review open Specialty Coating Systems send cartons across operators, reassign in-progress cartons when coverage changes, seal or unseal cartons when packing must be corrected, reprint sealed carton labels, and create potting purchase orders when every draft row is in a sealed carton. Use SGS internal potting when units are potted in-house without an SCS purchase order. This page does not clock labor — operators work on Pre-Encapsulation or QC Incidents.">
        Potting PO
      </PageTitleWithHelp>

      <div className="flex flex-wrap gap-2 border-b border-slate-200" role="tablist" aria-label="Potting send routes">
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === "scs"}
          className={cn(
            "-mb-px inline-flex items-center rounded-t-md border border-transparent px-3 py-2 text-sm font-medium",
            mainTab === "scs"
              ? "border-slate-200 border-b-white bg-white text-slate-900"
              : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
          )}
          onClick={() => setMainTab("scs")}
        >
          SCS vendor send
          <PottingTabCountBadge count={scsWaitingForPo} tone="info" />
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === "sgs"}
          className={cn(
            "-mb-px inline-flex items-center rounded-t-md border border-transparent px-3 py-2 text-sm font-medium",
            mainTab === "sgs"
              ? "border-slate-200 border-b-white bg-white text-slate-900"
              : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
          )}
          onClick={() => setMainTab("sgs")}
        >
          SGS internal
          <PottingTabCountBadge count={sgsAwaitingGrade} tone={sgsAwaitingGrade > 0 ? "info" : "muted"} />
        </button>
      </div>

      {mainTab === "scs" ? (
        <div role="tabpanel">
          <ScsVendorSendSection operators={operators} />
        </div>
      ) : (
        <div role="tabpanel" className="space-y-6">
          <InternalPottingSendPanel embedded />
          <InternalPottingReworkSendPanel />
        </div>
      )}
    </div>
  );
}
